import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface Signature {
  name: string;
  title: string;
  organization: string;
  department: string;
  email: string;
  phone: string;
  other: string[]; // Array of additional fields
}

interface SignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  signature: Signature;
  onSignatureUpdate: (signature: Signature) => void;
}

export default function SignatureModal({ 
  isOpen, 
  onClose, 
  signature, 
  onSignatureUpdate 
}: SignatureModalProps) {
  const [localSignature, setLocalSignature] = useState<Signature>({
    ...signature,
    other: signature.other || [] // Ensure other array exists
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  // Update local state when signature prop changes
  useEffect(() => {
    setLocalSignature({
      ...signature,
      other: signature.other || []
    });
    setErrors({});
  }, [signature]);

  // Handle escape key to close modal without bubbling
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape, true); // Use capture phase
      return () => document.removeEventListener('keydown', handleEscape, true);
    }
  }, [isOpen]);

  const updateSignatureMutation = useMutation({
    mutationFn: async (signatureData: Signature) => {
      return apiRequest("PUT", "/api/preferences/", {
        email_signature: JSON.stringify(signatureData)
      });
    },
    onSuccess: () => {
      toast({
        title: "Signature updated",
        description: "Your email signature has been saved.",
      });
      onSignatureUpdate(localSignature);
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update signature",
        description: error.message || "Something went wrong",
        variant: "destructive",
      });
    },
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!localSignature.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!localSignature.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(localSignature.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!validateForm()) return;
    updateSignatureMutation.mutate(localSignature);
  };

  const handleInputChange = (field: keyof Omit<Signature, 'other'>, value: string) => {
    setLocalSignature(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleOtherFieldChange = (index: number, value: string) => {
    setLocalSignature(prev => ({
      ...prev,
      other: prev.other.map((item, i) => i === index ? value : item)
    }));
  };

  const addOtherField = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setLocalSignature(prev => ({
      ...prev,
      other: [...prev.other, ""]
    }));
  };

  const removeOtherField = (index: number, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setLocalSignature(prev => ({
      ...prev,
      other: prev.other.filter((_, i) => i !== index)
    }));
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Reset to original signature on cancel
    setLocalSignature({
      ...signature,
      other: signature.other || []
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        // Close modal if clicking on backdrop
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          handleClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Edit Email Signature</h3>
          <button
            onClick={(e) => handleClose(e)}
            className="text-gray-400 hover:text-gray-600 text-xl font-bold w-6 h-6 flex items-center justify-center"
            disabled={updateSignatureMutation.isPending}
          >
            ×
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="sig-name" className="text-sm font-medium">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="sig-name"
              value={localSignature.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`mt-1 ${errors.name ? "border-red-500" : ""}`}
              placeholder="Enter your full name"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>
          
          <div>
            <Label htmlFor="sig-title" className="text-sm font-medium">Title</Label>
            <Input
              id="sig-title"
              value={localSignature.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className="mt-1"
              placeholder="e.g., Head Coach, Assistant Coach"
            />
          </div>
          
          <div>
            <Label htmlFor="sig-org" className="text-sm font-medium">Organization</Label>
            <Input
              id="sig-org"
              value={localSignature.organization}
              onChange={(e) => handleInputChange('organization', e.target.value)}
              className="mt-1"
              placeholder="e.g., UC Riverside, Stanford University"
            />
          </div>
          
          <div>
            <Label htmlFor="sig-dept" className="text-sm font-medium">Department</Label>
            <Input
              id="sig-dept"
              value={localSignature.department}
              onChange={(e) => handleInputChange('department', e.target.value)}
              className="mt-1"
              placeholder="e.g., Soccer Program, Athletics"
            />
          </div>
          
          <div>
            <Label htmlFor="sig-email" className="text-sm font-medium">
              Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="sig-email"
              type="email"
              value={localSignature.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`mt-1 ${errors.email ? "border-red-500" : ""}`}
              placeholder="your.email@university.edu"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>
          
          <div>
            <Label htmlFor="sig-phone" className="text-sm font-medium">Phone</Label>
            <Input
              id="sig-phone"
              value={localSignature.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="mt-1"
              placeholder="(555) 123-4567"
            />
          </div>

          {/* Dynamic Other Fields */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Additional Information</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => addOtherField(e)}
                className="text-xs px-2 py-1 h-7"
              >
                <i className="fas fa-plus text-xs mr-1"></i>
                Add Field
              </Button>
            </div>
            
            {localSignature.other.length === 0 && (
              <p className="text-xs text-gray-500 italic mb-2">
                Click "Add Field" to include additional information like website, LinkedIn, etc.
              </p>
            )}
            
            <div className="space-y-2">
              {localSignature.other.map((field, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Input
                    value={field}
                    onChange={(e) => handleOtherFieldChange(index, e.target.value)}
                    placeholder="e.g., Website: www.example.com, LinkedIn: linkedin.com/in/username"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => removeOtherField(index, e)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-8"
                  >
                    <i className="fas fa-trash text-xs"></i>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Preview Section */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Preview:</h4>
          <div className="text-xs text-gray-600 space-y-1">
            <p className="font-medium">{localSignature.name || "Your Name"}</p>
            {localSignature.title && <p>{localSignature.title}</p>}
            {localSignature.organization && <p>{localSignature.organization}</p>}
            {localSignature.department && <p>{localSignature.department}</p>}
            {localSignature.email && (
              <p className="text-blue-600 break-all">{localSignature.email}</p>
            )}
            {localSignature.phone && <p>{localSignature.phone}</p>}
            {localSignature.other.filter(field => field.trim()).map((field, index) => (
              <p key={index}>{field}</p>
            ))}
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="outline"
            onClick={(e) => handleClose(e)}
            disabled={updateSignatureMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={(e) => handleSave(e)}
            disabled={updateSignatureMutation.isPending}
          >
            {updateSignatureMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              "Save Signature"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}